import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageBanner from "@/components/PageBanner";
import SectionTitle from "@/components/SectionTitle";
import FinalCTA from "@/components/FinalCTA";
import { SERVICES, IMAGES } from "@/lib/content";
import { Truck, Globe2, FileCheck2, Car, KeyRound, CheckCircle2 } from "lucide-react";

const ICONS = { Truck, Globe2, FileCheck2, Car, KeyRound };

const Services = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.replace("#", ""));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    }
  }, [location]);

  return (
    <div className="min-h-screen flex flex-col bg-abayle-ivory">
      <Navbar />
      <main>
        <PageBanner
          eyebrow="Services"
          title="Our Services"
          subtitle="Five integrated services designed to support clients across movement, trade, clearance, and mobility."
          image={IMAGES.logistics}
        />

        <section className="bg-white" data-testid="services-all">
          <div className="container-abayle py-14 sm:py-20 lg:py-28">
            <SectionTitle
              centered
              eyebrow="Integrated Service Lines"
              title="Designed for Clients Who Value Reliability"
              subtitle="Whether you need to move cargo, navigate trade processes, or keep your team mobile across Somalia — Abayle delivers."
            />

            <div className="space-y-20 lg:space-y-28">
              {SERVICES.map((s, idx) => {
                const Icon = ICONS[s.icon];
                const reverse = idx % 2 === 1;
                return (
                  <div
                    key={s.key}
                    id={s.key}
                    className={`grid lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-16 items-center scroll-mt-32 ${reverse ? "lg:[&>div:first-child]:order-2" : ""}`}
                    data-testid={`service-detail-${s.key}`}
                  >
                    <div className="relative">
                      <div className={`absolute -top-4 ${reverse ? "-right-4" : "-left-4"} w-24 h-24 ${reverse ? "border-t-2 border-r-2" : "border-t-2 border-l-2"} border-abayle-gold z-10`} />
                      <div className={`absolute -bottom-4 ${reverse ? "-left-4" : "-right-4"} w-24 h-24 ${reverse ? "border-b-2 border-l-2" : "border-b-2 border-r-2"} border-abayle-gold z-10`} />
                      <img src={s.image} alt={s.title} className="w-full h-[260px] sm:h-[380px] lg:h-[460px] object-cover rounded-sm shadow-xl" />
                    </div>
                    <div>
                      <div className="flex items-center gap-3 sm:gap-4 mb-4">
                        <div className="h-12 w-12 sm:h-14 sm:w-14 rounded-md bg-abayle-green text-white flex items-center justify-center border-2 border-abayle-gold">
                          <Icon className="h-5 w-5 sm:h-6 sm:w-6" />
                        </div>
                        <span className="text-abayle-gold uppercase text-[10px] sm:text-xs tracking-[0.3em] font-bold">Service 0{idx + 1}</span>
                      </div>
                      <h3 className="relative text-2xl sm:text-3xl lg:text-4xl font-bold text-abayle-green pb-4 tracking-tight mb-5">
                        {s.title}
                        <span className="absolute bottom-0 left-0 w-12 sm:w-14 h-[3px] bg-abayle-gold" />
                      </h3>
                      <p className="text-abayle-charcoal/85 text-sm sm:text-base lg:text-lg leading-relaxed">{s.long}</p>

                      <ul className="mt-6 space-y-2">
                        {[
                          "Responsive communication and accountable support",
                          "Integrated coordination across service lines",
                          "Flexible engagement for projects or ongoing needs",
                        ].map((point) => (
                          <li key={point} className="flex items-start gap-3 text-abayle-charcoal/80">
                            <CheckCircle2 className="h-5 w-5 text-abayle-gold shrink-0 mt-0.5" />
                            <span className="text-sm lg:text-[15px]">{point}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <FinalCTA />
      </main>
      <Footer />
    </div>
  );
};

export default Services;
